import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Icon from '@/components/ui/icon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { getNavigationTreeForRole, type NavigationNode } from '@/constants/navigation';
import { useNavigationFavorites } from '@/hooks/useNavigationFavorites';
import { hasRequiredRole, normalizeUserRole } from '@/types/user';

const FOCUSABLE_ELEMENTS_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Sidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const user = useAuth();
  const userRole = normalizeUserRole(user?.role);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const [openTopLevel, setOpenTopLevel] = useState<string>('');
  const [openNestedAccordions, setOpenNestedAccordions] =
    useState<Record<string, string>>({});
  const { favoriteItems, removeFavorite } = useNavigationFavorites();
  const favoriteLinks = useMemo(
    () =>
      favoriteItems.filter((item) => hasRequiredRole(userRole, item.minRole)),
    [favoriteItems, userRole],
  );

  const filteredNavigationTree = useMemo(
    () => getNavigationTreeForRole(userRole),
    [userRole],
  );

  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!asideRef.current) {
      return [];
    }

    return Array.from(
      asideRef.current.querySelectorAll<HTMLElement>(
        FOCUSABLE_ELEMENTS_SELECTOR,
      ),
    );
  }, []);

  const handleClose = useCallback(() => {
    onClose();

    const previouslyFocusedElement = lastFocusedElementRef.current;

    if (
      previouslyFocusedElement &&
      typeof previouslyFocusedElement.focus === 'function'
    ) {
      previouslyFocusedElement.focus();
    }

    lastFocusedElementRef.current = null;
  }, [onClose]);

  const currentPath =
    location.pathname === '/'
      ? '/'
      : location.pathname.replace(/\/+$/, '');

  const isPathActive = useCallback(
    (path?: string) => {
      if (!path) {
        return false;
      }

      const normalizedTarget =
        path === '/' ? '/' : path.replace(/\/+$/, '');

      if (normalizedTarget === '/') {
        return matchPath({ path: '/', end: true }, currentPath) !== null;
      }

      return (
        matchPath({ path: normalizedTarget, end: true }, currentPath) !==
          null ||
        matchPath({ path: `${normalizedTarget}/*` }, currentPath) !== null
      );
    },
    [currentPath],
  );

  const isMenuItemActive = useCallback(
    (menuItem: NavigationNode): boolean => {
      if (menuItem.path && isPathActive(menuItem.path)) {
        return true;
      }

      return menuItem.items?.some((child) => isMenuItemActive(child)) ?? false;
    },
    [isPathActive],
  );

  useEffect(() => {
    const activeAccordionItem = filteredNavigationTree.find(
      (menuItem) => menuItem.items && isMenuItemActive(menuItem),
    );

    setOpenTopLevel(activeAccordionItem?.label ?? '');

    setOpenNestedAccordions(() => {
      const updated: Record<string, string> = {};

      filteredNavigationTree.forEach((menuItem) => {
        if (!menuItem.items) {
          return;
        }

        const activeNested = menuItem.items.find(
          (subItem) => subItem.items && isMenuItemActive(subItem),
        );

        updated[menuItem.label] = activeNested?.label ?? '';
      });

      return updated;
    });
  }, [filteredNavigationTree, isMenuItemActive]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const activeElement = document.activeElement;

    if (
      activeElement instanceof HTMLElement &&
      !asideRef.current?.contains(activeElement)
    ) {
      lastFocusedElementRef.current = activeElement;
    }

    const focusableElements = getFocusableElements();
    const focusTarget = closeButtonRef.current ?? focusableElements[0];

    focusTarget?.focus();
  }, [getFocusableElements, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
        return;
      }

      if (!open || event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements();

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!(activeElement instanceof HTMLElement)) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      const isInSidebar = asideRef.current?.contains(activeElement) ?? false;

      if (event.shiftKey) {
        if (!isInSidebar || activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }

        return;
      }

      if (!isInSidebar || activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    },
    [getFocusableElements, handleClose, open],
  );

  return (
    <>
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity duration-200 ${
          open
            ? 'md:hidden opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        tabIndex={-1}
        role="presentation"
        aria-hidden="true"
      />

      <aside
        ref={asideRef}
        className={`fixed top-0 left-0 h-full z-50 bg-gradient-to-b from-blue-900 to-blue-950 text-white w-56 min-w-56 flex flex-col
          transform transition-transform duration-200
          ${open ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:static md:flex
        `}
        style={{ maxWidth: '100vw' }}
        onKeyDown={handleKeyDown}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
      >
        <div
          className="shrink-0 p-5 font-bold text-xl tracking-wide bg-gradient-to-b from-blue-950 to-blue-900 border-b border-blue-900 shadow-sm flex items-center justify-between gap-4"
          style={{
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
            letterSpacing: '0.04em',
          }}
        >
          <span>StenPro</span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            aria-label="Stäng meny"
            className="p-2 -mr-3 rounded-md text-blue-100 transition hover:text-white hover:bg-blue-800/70 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <Icon name="X" />
          </button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto max-h-full flex flex-col gap-1 mt-2">
          {/* Allow only one accordion item to be open at a time */}
          {favoriteLinks.length > 0 ? (
            <div className="px-5 pb-2 flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-100/80">
                Favoriter
              </span>
              {favoriteLinks.map((item) => {
                const active = isPathActive(item.path);
                const description =
                  item.parents.length > 0 ? item.parents.join(' / ') : undefined;

                return (
                  <div
                    key={item.path}
                    className="group flex items-center gap-2 rounded-md"
                  >
                    <Link
                      to={item.path}
                      onClick={handleClose}
                      aria-current={active ? 'page' : undefined}
                      className={`relative flex flex-1 items-center gap-3 px-3 py-2 text-base font-semibold transition rounded-md ${
                        active
                          ? 'bg-blue-800/80 text-white'
                          : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'
                      }`}
                    >
                      {active && (
                        <span className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-sm" />
                      )}
                      {item.icon && <Icon name={item.icon} />}
                      <span className="flex flex-col">
                        <span>{item.label}</span>
                        {description ? (
                          <span className="text-xs font-medium text-blue-100/70">
                            {description}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => removeFavorite(item.path)}
                      aria-label={`Ta bort ${item.label} från favoriter`}
                      className="p-1 text-blue-100 transition hover:text-yellow-300 focus:outline-none focus:ring-2 focus:ring-white/60 rounded"
                    >
                      <Icon
                        name="Star"
                        size={18}
                        className="text-yellow-300"
                        fill="currentColor"
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}

          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={openTopLevel}
            onValueChange={(value) =>
              setOpenTopLevel(value ?? '')
            }
          >
            {filteredNavigationTree.map((item) => {
              if (item.items) {
                const isActive = isMenuItemActive(item);
                return (
                  <AccordionItem value={item.label} key={item.label}>
                    <AccordionTrigger
                      className={`
                        group flex items-center w-full px-5 py-2 font-semibold text-base rounded-md transition
                        justify-between
                        ${isActive ? 'bg-blue-800/80 text-white' : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'}
                      `}
                      style={{ textAlign: 'left' }}
                    >
                      <span className="flex items-center gap-3">
                        {item.icon && <Icon name={item.icon} />}
                        <span>{item.label}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pl-8 flex flex-col gap-1">
                      {item.items.map((sub) => {
                        if (sub.items) {
                          const subActive = isMenuItemActive(sub);
                          return (
                            <Accordion
                              type="single"
                              collapsible
                              key={sub.label}
                              className="w-full"
                              value={openNestedAccordions[item.label] ?? ''}
                              onValueChange={(value) =>
                                setOpenNestedAccordions((prev) => ({
                                  ...prev,
                                  [item.label]: value ?? '',
                                }))
                              }
                            >
                              <AccordionItem value={sub.label}>
                                <AccordionTrigger
                                  className={`
                                    group flex items-center w-full px-2 py-1 font-medium text-base rounded-md transition
                                    justify-between
                                    ${subActive ? 'bg-blue-700/80 text-white' : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'}
                                  `}
                                  style={{ textAlign: 'left' }}
                                >
                                  <span className="flex items-center gap-3">
                                    {sub.icon && <Icon name={sub.icon} />}
                                    <span>{sub.label}</span>
                                  </span>
                                </AccordionTrigger>
                                <AccordionContent className="pl-6 flex flex-col gap-1">
                                  {sub.items.map((child) => {
                                    const active = isPathActive(child.path);
                                    return (
                                      <Link
                                        key={child.path}
                                        to={child.path!}
                                        onClick={handleClose}
                                        aria-current={active ? 'page' : undefined}
                                        className={`relative flex items-center gap-2 px-2 py-1 rounded-md font-medium transition text-base
                                          ${active ? 'bg-blue-700/80 text-white' : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'}
                                        `}
                                      >
                                        {active && (
                                          <span className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-sm" />
                                        )}
                                        <span>{child.label}</span>
                                      </Link>
                                    );
                                  })}
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          );
                        }

                        const active = isPathActive(sub.path);
                        return (
                          <Link
                            key={sub.path}
                            to={sub.path!}
                            onClick={handleClose}
                            aria-current={active ? 'page' : undefined}
                            className={`relative flex items-center gap-2 px-2 py-1 rounded-md font-medium transition text-base
                              ${active ? 'bg-blue-700/80 text-white' : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'}
                            `}
                          >
                            {active && (
                              <span className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-sm" />
                            )}
                            <span>{sub.label}</span>
                          </Link>
                        );
                      })}
                    </AccordionContent>
                  </AccordionItem>
                );
              }

              const isActive = isPathActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path!}
                  onClick={handleClose}
                  aria-current={isActive ? 'true' : undefined}
                  className={`relative flex items-center gap-3 px-5 py-2 font-semibold text-base rounded-md transition
                    ${isActive ? 'bg-blue-800/80 text-white' : 'text-blue-100 hover:bg-blue-800/70 hover:text-white'}
                  `}
                  style={{ textAlign: 'left' }}
                >
                  {isActive && (
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-sm" />
                  )}
                  {item.icon && <Icon name={item.icon} />}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </Accordion>
        </nav>

        <div className="shrink-0 border-t border-blue-800 mt-auto p-5 text-sm opacity-80 bg-blue-950/80">
          <div className="font-semibold">AB Svenska Stenhus</div>
          <div>{user?.displayName ?? 'Gäst'}</div>
        </div>
      </aside>
    </>
  );
}
